import React from 'react'
import { cn } from '@/lib/utils'
import Reveal3 from '@/components/marketing3/Reveal3'
import SyncCard from '@/components/marketing3/widgets/SyncCard'
import CalendarPeek from '@/components/marketing3/widgets/CalendarPeek'
import GoLiveSwitch from '@/components/marketing3/widgets/GoLiveSwitch'
import ChangeLogTicker from '@/components/marketing3/widgets/ChangeLogTicker'

/**
 * HowItWorks3 — the light, enriched four-step section the founder liked. Keeps v1's
 * numbered-step rail feel (mono numerals 01–04) but richer: a hairline spine threads
 * the four setup steps, and each step is paired with its simulated widget vignette
 * (W2–W5) on an ALTERNATING rail — copy left/widget right, then flipped.
 *
 * Step numerals 01–04 are FINE here: these are setup steps, not rate-engine pipeline
 * stages — no stage-count secret is leaked.
 *
 * All visible copy is VERBATIM from docs/marketing/2026-07-05-landing3-copy.md
 * §How it works — do not edit sentences here without updating that doc + the claims
 * register, which is the gate every marketing sentence passes ("no marketing sentence
 * ships unless its claim is in this register, with a status"): a sentence edited only in
 * this file is a published claim nobody graded, and the grade is what we would defend if
 * it were challenged. Nothing enforces this; it is a habit, not a test.
 * Governance held per the plan's §0 do-not-publish list: PMS-neutral
 * ("your PMS" only), no compute-frequency language, no history-depth figure, no
 * push read-back claim, no pipeline count. {BRAND} → `brand` prop.
 */

interface Step {
  n: string
  title: string
  body: (brand: string) => React.ReactNode
  widget: React.ReactElement<{ className?: string }>
}

const STEPS: Step[] = [
  {
    n: '01',
    title: 'Connect your PMS.',
    body: () => (
      <>
        A guided setup connects your PMS, syncs your rooms and rate plans, and learns from your booking history from
        day one — so it starts smart, not blank.
      </>
    ),
    widget: <SyncCard />,
  },
  {
    n: '02',
    title: 'Preview everything.',
    body: () => (
      <>
        Every recommendation appears on a full live calendar before a single rate touches your PMS. Hover any date and
        read the Rate Ledger — which rule moved the price, and by how much. Nothing is pushed until you say so.
      </>
    ),
    widget: <CalendarPeek />,
  },
  {
    n: '03',
    title: 'Go live when you’re ready.',
    body: () => (
      <>
        Set your Price Guardrails — the floor and ceiling no rate will ever cross — and flip the switch per property,
        when the numbers have earned your trust. We never flip it for you.
      </>
    ),
    widget: <GoLiveSwitch />,
  },
  {
    n: '04',
    title: 'It prices. You stay in charge.',
    body: () => (
      <>
        From then on the engine works your calendar day and night, and every change lands in the Change Log: what
        changed, when, and why.
      </>
    ),
    widget: <ChangeLogTicker />,
  },
]

export default function HowItWorks3({ brand }: { brand: string }) {
  return (
    <section
      id="how-it-works"
      aria-labelledby="hiw3-heading"
      aria-label={`How ${brand} works`}
      className="relative scroll-mt-24"
    >
      <div className="mx-auto max-w-6xl px-4 py-24 sm:px-6 sm:py-28">
        {/* Heading */}
        <Reveal3 className="max-w-2xl">
          <p className="mkt3-eyebrow">How it works</p>
          <h2 id="hiw3-heading" className="mkt3-display mkt3-h2 mt-4">
            From first sync to fully in charge.
          </h2>
        </Reveal3>

        {/* The stepped rail — a hairline spine on the left threads mono numerals
            01–04 down the section (v1's liked rail feel); each step pairs its copy
            with its widget on an alternating left/right layout at lg. */}
        <ol className="relative mt-16 space-y-16 sm:mt-20 sm:space-y-24 sm:pl-20">
          {/* Hairline spine — decorative, sits behind the numeral nodes. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-8 left-[1.375rem] top-5 hidden w-px bg-gradient-to-b from-teal-500/30 via-[var(--mkt3-hairline)] to-transparent sm:block"
          />

          {STEPS.map((step, i) => {
            const flipped = i % 2 === 1
            return (
              <Reveal3 as="li" key={step.n} delay={40} className="relative">
                {/* Numeral node — sits on the spine (sm+). A ring of paper masks
                    the spine behind it so the numeral reads as a station on the line. */}
                <span
                  aria-hidden="true"
                  className="absolute left-[-4.625rem] top-0 z-10 hidden h-11 w-11 items-center justify-center rounded-full border border-teal-600/25 bg-teal-50 font-mono text-sm font-semibold text-teal-700 shadow-[0_0_0_6px_var(--mkt3-paper)] sm:flex"
                >
                  {step.n}
                </span>

                <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14">
                  {/* Copy column */}
                  <div className={cn(flipped && 'lg:order-2')}>
                    <h3 className="mkt3-h3 flex items-baseline gap-2.5 font-semibold text-[var(--mkt3-ink)]">
                      <span aria-hidden="true" className="font-mono text-sm font-semibold text-teal-700 sm:hidden">
                        {step.n}
                      </span>
                      {step.title}
                    </h3>
                    <p className="mkt3-prose mt-4">{step.body(brand)}</p>
                  </div>

                  {/* Widget column */}
                  <div className={cn('flex', flipped ? 'lg:order-1 lg:justify-start' : 'lg:justify-end')}>
                    {React.cloneElement(step.widget, { className: 'mkt3-lift w-full max-w-md' })}
                  </div>
                </div>
              </Reveal3>
            )
          })}
        </ol>
      </div>
    </section>
  )
}
